import React, {useState, useEffect, useMemo} from 'react'
import {useSelector} from 'react-redux'
import {styled} from '@material-ui/core/styles'
import MuiPaper from '@material-ui/core/Paper'
import {getApplicationsState} from 'lms/store/selectors'
import {useCurrentSearchResults} from 'searches/hooks'
import Card from 'lms/components/Card'
import RfpDialog from 'rfps/components/Dialog'
import {useMode} from 'app/hooks'
import CompareCard from 'lms/components/Compare/Card'
import CreateAppButton from './CreateAppButton'
import Loader from '../Loader'
import SortBy from './SortBy'
import LazyLoad, {forceCheck} from 'react-lazyload'

const Content = styled('ul')({
  flex: '0 1 auto',
  margin: 0,
  padding: 0,
  listStyleType: 'none'
})

const ListHeader = styled('li')(({theme}) => ({}))

const Split = styled('div')(({theme}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1)
}))

const Count = styled('p')(({theme}) => ({
  margin: 0,
  color: theme.palette.primary.contrastText,
  fontSize: 20
}))

const ListItem = styled('li')({
  marginBottom: 20
})

const EmptyList = styled(MuiPaper)({
  padding: 50,
  fontSize: 18,
  textAlign: 'center',
  width: '100%'
})

const LmsCardComponent = ({lms}) => (
  <ListItem>
    <Card lms={lms} />
  </ListItem>
)

const shuffleArray = arr => {
  const shuffled = arr.slice()
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

const shuffleCache = new Map()

const List = ({hasFetchedApplications}) => {
  const {
    data: {allIds, byId, platinumIds, premiumIds}
  } = useSelector(getApplicationsState)
  const filteredIds = useCurrentSearchResults()

  const [sortType, setSortType] = useState('default')

  const [mode] = useMode()
  
  // Force LazyLoad to check for newly visible elements when filters change
  useEffect(() => {
    // Small delay to ensure DOM has updated
    const timer = setTimeout(() => {
      forceCheck()
    }, 100)
    
    return () => clearTimeout(timer)
  }, [filteredIds])

  // Safely check if an application exists and has the specified property
  const safeGet = (id, prop) => {
    const app = byId[id]
    return app && app[prop] !== undefined ? app[prop] : null
  }

  const updatedAtNewSorter = (a, b) => {
    const aDate = safeGet(a, 'updatedAt')
    const bDate = safeGet(b, 'updatedAt')
    if (!aDate && !bDate) return 0
    if (!aDate) return 1
    if (!bDate) return -1
    return new Date(bDate) - new Date(aDate)
  }

  const updatedAtOldSorter = (a, b) => {
    const aDate = safeGet(a, 'updatedAt')
    const bDate = safeGet(b, 'updatedAt')
    if (!aDate && !bDate) return 0
    if (!aDate) return 1
    if (!bDate) return -1
    return new Date(aDate) - new Date(bDate)
  }

  const ascendingAlphaSorter = (a, b) => {
    const aName = safeGet(a, 'name')
    const bName = safeGet(b, 'name')
    if (!aName && !bName) return 0
    if (!aName) return 1
    if (!bName) return -1
    return aName.localeCompare(bName)
  }

  const descendingAlphaSorter = (a, b) => {
    const aName = safeGet(a, 'name')
    const bName = safeGet(b, 'name')
    if (!aName && !bName) return 0
    if (!aName) return 1
    if (!bName) return -1
    return bName.localeCompare(aName)
  }

  /* Craig's Ratings - Start*/
  const averageRatingSorter = (a, b) => {
    const aLms = byId[a]
    const bLms = byId[b]
    
    // Check if applications exist
    if (!aLms && !bLms) return 0
    if (!aLms) return 1
    if (!bLms) return -1
    
    const aLmsCraigsRatings = aLms.craigsRatings
    const bLmsCraigsRatings = bLms.craigsRatings
    
    if (aLmsCraigsRatings && bLmsCraigsRatings) {
      return bLmsCraigsRatings.average - aLmsCraigsRatings.average
    } else if (aLmsCraigsRatings && !bLmsCraigsRatings) {
      return -1 // a has ratings, b doesn't, so a comes first
    } else if (bLmsCraigsRatings && !aLmsCraigsRatings) {
      return 1 // b has ratings, a doesn't, so b comes first
    }
    return 0 // Neither has ratings
  }

  const sortByAverageRating = idsToSort => {
    const rankedIds = idsToSort.reduce(
      (acc, id) => {
        const lms = byId[id]
        // Check if application exists
        if (!lms) return acc
        
        const craigsRatings = lms.craigsRatings
        const hasRankings = Boolean(craigsRatings)
        if (hasRankings) {
          acc.hasRanking.push(id)
        } else {
          acc.noRanking.push(id)
        }
        return acc
      },
      {
        hasRanking: [],
        noRanking: []
      }
    )
    const sortedRanked = rankedIds.hasRanking.slice().sort(averageRatingSorter)
    const sortedAlpha = rankedIds.noRanking.slice().sort(ascendingAlphaSorter)
    return [...sortedRanked, ...sortedAlpha]
  }
  /* Craig's Ratings - End*/

  /* Craig's Best In Class - Start*/
  const averageRankingSorter = (a, b) => {
    const aLms = byId[a]
    const bLms = byId[b]
    
    // Check if applications exist
    if (!aLms && !bLms) return 0
    if (!aLms) return 1
    if (!bLms) return -1
    
    const aLmsRanking = aLms.ranking
    const bLmsRanking = bLms.ranking
    
    if (aLmsRanking && bLmsRanking) {
      return bLmsRanking - aLmsRanking
    } else if (aLmsRanking && !bLmsRanking) {
      return -1 // a has ranking, b doesn't, so a comes first
    } else if (bLmsRanking && !aLmsRanking) {
      return 1 // b has ranking, a doesn't, so b comes first
    }
    return 0 // Neither has ranking
  }

  const sortByAverageRanking = idsToSort => {
    const rankedIds = idsToSort.reduce(
      (acc, id) => {
        const lms = byId[id]
        // Check if application exists
        if (!lms) return acc
        
        const rank = lms.rank
        const hasRankings = Boolean(rank)
        if (hasRankings) {
          acc.hasRanking.push(id)
        } else {
          acc.noRanking.push(id)
        }
        return acc
      },
      {
        hasRanking: [],
        noRanking: []
      }
    )
    const sortedRanked = rankedIds.hasRanking.slice().sort(averageRankingSorter)
    const sortedAlpha = rankedIds.noRanking.slice().sort(ascendingAlphaSorter)
    return [...sortedRanked, ...sortedAlpha]
  }
  /* Craig's Best In Class - End*/

  const displayedIds = filteredIds || allIds
		
  const getSortedIds = () => {
    const allPlatinumIds = displayedIds.filter(displayId =>
      platinumIds.includes(displayId)
    )
    const allPremiumIds = displayedIds.filter(displayId =>
      !platinumIds.includes(displayId) && premiumIds.includes(displayId)
    )
    const allNonPremiumIds = displayedIds.filter(
      displayId => !platinumIds.includes(displayId) && !premiumIds.includes(displayId)
    )
    let platinumSorted, premiumSorted, nonPremiumSorted
    switch (sortType) {
      case 'default': {
        const cacheKey = displayedIds.join(',')
        if (!shuffleCache.has(cacheKey)) {
          shuffleCache.set(cacheKey, {
            platinum: shuffleArray(allPlatinumIds),
            premium: shuffleArray(allPremiumIds),
            nonPremium: shuffleArray(allNonPremiumIds)
          })
        }
        ;({platinum: platinumSorted, premium: premiumSorted, nonPremium: nonPremiumSorted} = shuffleCache.get(cacheKey))
        break
      }
      case 'A-Z':
        platinumSorted = allPlatinumIds.slice().sort(ascendingAlphaSorter)
        premiumSorted = allPremiumIds.slice().sort(ascendingAlphaSorter)
        nonPremiumSorted = allNonPremiumIds.slice().sort(ascendingAlphaSorter)
        break
      case 'Z-A':
        platinumSorted = allPlatinumIds.slice().sort(descendingAlphaSorter)
        premiumSorted = allPremiumIds.slice().sort(descendingAlphaSorter)
        nonPremiumSorted = allNonPremiumIds.slice().sort(descendingAlphaSorter)
        break
      case 'updated-newest':
        platinumSorted = allPlatinumIds.slice().sort(updatedAtNewSorter)
        premiumSorted = allPremiumIds.slice().sort(updatedAtNewSorter)
        nonPremiumSorted = allNonPremiumIds.slice().sort(updatedAtNewSorter)
        break
      case 'updated-oldest':
        platinumSorted = allPlatinumIds.slice().sort(updatedAtOldSorter)
        premiumSorted = allPremiumIds.slice().sort(updatedAtOldSorter)
        nonPremiumSorted = allNonPremiumIds.slice().sort(updatedAtOldSorter)
        break
      case 'rank':
        platinumSorted = sortByAverageRanking(allPlatinumIds)
        premiumSorted = sortByAverageRanking(allPremiumIds)
        nonPremiumSorted = sortByAverageRanking(allNonPremiumIds)
        break
      case 'average':
        platinumSorted = sortByAverageRating(allPlatinumIds)
        premiumSorted = sortByAverageRating(allPremiumIds)
        nonPremiumSorted = sortByAverageRating(allNonPremiumIds)
        break
      default:
        //A-Z is the default
        platinumSorted = allPlatinumIds.slice().sort(ascendingAlphaSorter)
        premiumSorted = allPremiumIds.slice().sort(ascendingAlphaSorter)
        nonPremiumSorted = allNonPremiumIds.slice().sort(ascendingAlphaSorter)
        break
    }
				
    return [...platinumSorted, ...premiumSorted, ...nonPremiumSorted]
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sortedIds = useMemo(getSortedIds, [sortType, displayedIds, byId, platinumIds, premiumIds])

  const lmsIdsToDisplay = sortedIds.filter(displayId =>
    displayedIds.includes(displayId)
  )

  const renderLmsCards = () => {
    // Count visible items to determine if we should use LazyLoad
    const visibleCount = lmsIdsToDisplay.filter(lmsId => {
      const lms = byId[lmsId]
      if (!lms) return false
      const shouldHide = (mode === 'user' || mode === 'User') && !lms.isPublished
      return !shouldHide
    }).length
    
    // For small result sets, disable LazyLoad to ensure all items are visible
    const useLazyLoad = visibleCount > 10
    
    return lmsIdsToDisplay.reduce((acc, lmsId) => {
      const lms = byId[lmsId]
      
      // Skip if lms is undefined (e.g., it was deleted)
      if (!lms) return acc
      
      // hide unpublished systems from users
      const shouldHide =
        (mode === 'user' || mode === 'User') && !lms.isPublished
      if (!shouldHide) {
        // If using LazyLoad, wrap the component
        if (useLazyLoad) {
          acc.push(
            <LazyLoad
              key={lmsId}
              height={100}
              offset={2000}
              overflow
              once
              placeholder={<div className="lazyload-placeholder" style={{minHeight: '100px'}}>Loading...</div>}
              // Force visible when filtering is active to ensure content appears
              scrollContainer={filteredIds ? window : undefined}
            >
              <LmsCardComponent lms={lms} key={lmsId} />
            </LazyLoad>
          )
        } else {
          // For small result sets, render directly without LazyLoad
          acc.push(<LmsCardComponent lms={lms} key={lmsId} />)
        }
      }
      return acc
    }, [])
  }

  // Count only applications that exist in byId and should be shown based on user role
  const visibleAppsCount = lmsIdsToDisplay.reduce((count, lmsId) => {
    const lms = byId[lmsId]
    if (!lms) return count
    
    // Don't count unpublished systems for non-admin users
    const shouldHide = (mode === 'user' || mode === 'User') && !lms.isPublished
    return shouldHide ? count : count + 1
  }, 0)

  const resultString = visibleAppsCount === 1 ? 'result' : 'results'
  const countString = `${visibleAppsCount} ${resultString}`
  const isAdmin = mode === 'admin'

  const createAppButton = isAdmin ? <CreateAppButton /> : null

  const isLoaded = hasFetchedApplications && Boolean(allIds)
  const noAppsToShow = visibleAppsCount === 0

  return isLoaded ? (
    noAppsToShow ? (
      <EmptyList>No Applications to Show</EmptyList>
    ) : (
      <>
        <Content>
          <ListHeader>
            <Split>
              <Count>{countString}</Count>
              <SortBy handleSortTypeChange={setSortType} sortType={sortType} />
              {createAppButton}
            </Split>
            <CompareCard />
          </ListHeader>
          {renderLmsCards()}
          <RfpDialog />
        </Content>
      </>
    )
  ) : (
    <Loader />
  )
}

export default List
